// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * PWA Install Prompt Component
 * Shows a banner prompting users to install the app
 */

import { useState, useEffect } from 'react';
import { logger } from '../lib/logger';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Helper to check if recently dismissed (called during initial render)
function checkRecentlyDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  const dismissedAt = localStorage.getItem('pwa-install-dismissed');
  if (dismissedAt) {
    const daysSinceDismiss = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
    return daysSinceDismiss < 7;
  }
  return false;
}

// Helper to check if already installed
function checkIsInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches;
}

export function InstallPrompt() {
  // Temporarily disabled for development
  return null;

  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  // Initialize with computed values to avoid setState in effects
  const [isInstalled, setIsInstalled] = useState(checkIsInstalled);
  const [shouldHideDueToRecentDismiss] = useState(checkRecentlyDismissed);

  // Log installed state on mount
  useEffect(() => {
    if (isInstalled) {
      logger.info('App is already installed');
    }
  }, [isInstalled]);

  useEffect(() => {
    // Skip if already installed
    if (isInstalled) return;

    // Listen for beforeinstallprompt event
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);

      // Show prompt after delay (don't interrupt immediately)
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);

      logger.info('Install prompt available');
    };

    // Listen for successful install
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      logger.info('App installed successfully');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    logger.info('User clicked install button');
    await deferredPrompt.prompt();

    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      logger.info('User accepted install prompt');
    } else {
      logger.info('User dismissed install prompt');
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    logger.info('User dismissed install banner');

    // Store dismissal in localStorage to not show again for a while
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Don't show if already installed, prompt not available, user dismissed, or recently dismissed
  if (isInstalled || !showPrompt || !deferredPrompt || shouldHideDueToRecentDismiss) {
    return null;
  }

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg animate-slide-up"
      data-testid="install-prompt"
    >
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="text-2xl">&#128241;</div>
          <div>
            <h3 className="font-bold text-lg">Install Undef Engine</h3>
            <p className="text-sm opacity-90">Add to your home screen for the best experience</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleDismiss}
            data-testid="install-dismiss"
            className="px-4 py-2 text-sm font-medium bg-transparent border border-white/50 rounded-lg hover:bg-white/10 transition-colors"
          >
            Not Now
          </button>
          <button
            onClick={handleInstall}
            data-testid="install-button"
            className="px-4 py-2 text-sm font-bold bg-white text-indigo-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Install
          </button>
        </div>
      </div>
    </div>
  );
}

export default InstallPrompt;
